import { Fragment, useId, useState } from "react";
import { Link } from "react-router-dom";
import { LucideIcon } from "lucide-react";

import { cn } from "@/utils/utils";
import { buttonVariants } from "@/components/ui/button-variants";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import CounterBadge from "@/components/ui/counter-badge";
import { Divider } from "@/components/ui/divider";

interface NavProps {
  currentPageName?: string;
  isCollapsed: boolean;
  onPageClick: (targetName: string) => void;
  links: {
    title: string;
    icon: LucideIcon | string;
    variant: "default" | "ghost";
    target: string;
    count?: number;
    countTooltip?: string;
    dividerAfter?: boolean;
  }[];
}

export function Nav({
  currentPageName = "/",
  onPageClick,
  links,
  isCollapsed
}: NavProps) {
  const linkToDataTestId = (link: string): string =>
    link === "/" ? "home" : `${link}-link`;
  // Focus and the description go through the link, so the badge isn't a nested tab stop
  const countDescriptionIdPrefix = useId();
  const [hoveredCountTarget, setHoveredCountTarget] = useState<string | null>(
    null
  );
  const [keyboardFocusedTarget, setKeyboardFocusedTarget] = useState<
    string | null
  >(null);

  return (
    <div
      data-collapsed={isCollapsed}
      className="group flex flex-col gap-4 bg-surface-sidebar-navigation text-h2"
    >
      <nav className="grid px-2 group-[[data-collapsed=true]]:justify-start group-[[data-collapsed=true]]:px-2">
        {links.map((link) => (
          <Fragment key={link.target}>
            {isCollapsed ? (
              <Tooltip delayDuration={600}>
                <TooltipTrigger asChild>
                  <Link
                    to={link.target}
                    data-test={linkToDataTestId(link.target)}
                    className={cn(
                      buttonVariants({ size: "lg" }),
                      "size-9 py-2 relative",
                      "dark:bg-surface-sidebar-navigation dark:text-secondary dark:hover:bg-surface-hover-soft dark:hover:text-primary shadow-none",
                      currentPageName === link.target &&
                        "dark:bg-buttons-nav-item-selected dark:text-cta dark:hover:bg-buttons-nav-item-selected"
                    )}
                    onClick={() => {
                      onPageClick(link.target);
                    }}
                  >
                    <IconContainer
                      size="sm"
                      color="current"
                      className={cn(
                        currentPageName === link.target && "dark:text-cta"
                      )}
                    >
                      <link.icon />
                    </IconContainer>
                    <span className="sr-only">{link.title}</span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="flex items-center gap-4"
                >
                  {link.title}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to={link.target}
                data-test={linkToDataTestId(link.target)}
                className={cn(
                  buttonVariants({ size: "lg" }),
                  "dark:bg-surface-sidebar-navigation dark:text-secondary dark:hover:bg-surface-hover-soft dark:hover:text-primary font-normal shadow-none",
                  "justify-start",
                  currentPageName === link.target &&
                    "dark:bg-buttons-nav-item-selected dark:text-cta dark:hover:bg-buttons-nav-item-selected"
                )}
                aria-describedby={
                  link.count && link.countTooltip
                    ? `${countDescriptionIdPrefix}-${link.target}`
                    : undefined
                }
                onClick={() => {
                  onPageClick(link.target);
                }}
                onFocus={(event) => {
                  if (
                    link.count &&
                    link.countTooltip &&
                    event.currentTarget.matches(":focus-visible")
                  ) {
                    setKeyboardFocusedTarget(link.target);
                  }
                }}
                onBlur={() => setKeyboardFocusedTarget(null)}
              >
                <IconContainer
                  size="sm"
                  color="current"
                  className={cn(
                    "mr-2",
                    currentPageName === link.target && "dark:text-cta"
                  )}
                >
                  <link.icon />
                </IconContainer>
                <Text
                  as="span"
                  variant="body"
                  className={cn(
                    "text-primary flex-1",
                    currentPageName === link.target && "dark:text-cta"
                  )}
                >
                  {link.title}
                </Text>
                {!!link.count &&
                  (link.countTooltip ? (
                    <Tooltip
                      open={
                        hoveredCountTarget === link.target ||
                        keyboardFocusedTarget === link.target
                      }
                      onOpenChange={(open) => {
                        setHoveredCountTarget(open ? link.target : null);
                        if (!open) setKeyboardFocusedTarget(null);
                      }}
                    >
                      <TooltipTrigger asChild>
                        <span
                          data-test={`${linkToDataTestId(link.target)}-count`}
                        >
                          <CounterBadge count={link.count} size="sm" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" enableMarkdown={false}>
                        {link.countTooltip}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <CounterBadge count={link.count} size="sm" />
                  ))}
              </Link>
            )}
            {!isCollapsed && !!link.count && link.countTooltip && (
              <span
                id={`${countDescriptionIdPrefix}-${link.target}`}
                className="sr-only"
              >
                {link.countTooltip}
              </span>
            )}
            {link.dividerAfter && (
              <Divider className="mx-1" dividerYPadding="8" />
            )}
          </Fragment>
        ))}
      </nav>
    </div>
  );
}
